import { Suspense, lazy } from "react";
import { Navigate, useRoutes } from "react-router-dom";
import AuthGuard from "../guards/AuthGuard";
import AdminGuard from "../guards/AdminGuard";
import GuestGuard from "src/guards/GuestGuard";
import DashboardLayout from "../layouts/dashboard";
import UrbiaLoading from "../components/UrbiaLoading";
import Checkout from "src/pages/checkout/pages/Checkout";
import Crm from "src/pages/crm/Crm";
import RecoveryPassword from "src/pages/recovery-password/pages/RecoveryPassword";

const Loadable = (Component: any) => (props: any) => {
  return (
    <Suspense fallback={<UrbiaLoading />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: "login",
      element: (
        <GuestGuard>
          <Login />
        </GuestGuard>
      ),
    },
    {
      path: "register",
      element: (
        <GuestGuard>
          <Register />
        </GuestGuard>
      ),
    },
    {
      path: "recovery-password",
      element: (
        <GuestGuard>
          <RecoveryPassword />
        </GuestGuard>
      ),
    },
    {
      path: "profile",
      element: (
        <AuthGuard>
          <ProfilePage />
        </AuthGuard>
      ),
    },
    {
      path: "/",
      element: <Navigate to="/dashboard" replace />,
    },
    {
      path: "/home",
      element: <Navigate to="/dashboard" replace />,
    },
    {
      path: "/crm",
      element: (
        <AdminGuard>
          <DashboardLayout />
        </AdminGuard>
      ),
      children: [
        { element: <Navigate to="/crm/home" replace />, index: true },
        { path: "home", element: <Crm /> },
      ],
    },
    {
      path: "/viability",
      element: (
        <AuthGuard>
          <ViabilityStudy />
        </AuthGuard>
      ),
    },
    {
      path: "/viability-with-file",
      element: (
        <AuthGuard>
          <ViabilityStudyKMZ />
        </AuthGuard>
      ),
    },
    {
      path: "/checkout/:studyId",
      element: (
        <AuthGuard>
          <Checkout />
        </AuthGuard>
      ),
    },
    {
      path: "/pdf/:studyId",
      element: (
        <AuthGuard>
          <PDFPage />
        </AuthGuard>
      ),
    },
    {
      path: "/newVersion/:studyId",
      element: (
        <AuthGuard>
          <NewVersion />
        </AuthGuard>
      ),
    },
    {
      path: "/kmzerror",
      element: (
        <AuthGuard>
          <KmzError />
        </AuthGuard>
      ),
    },
    {
      path: "/dashboard",
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to="/dashboard/studies" replace />, index: true },
        { path: "studies", element: <Studies /> },
        { path: "studies/:studyId", element: <Study /> },
        { path: "group-version/:groupVersionId", element: <VersionGroup /> },
      ],
    },
    {
      path: "*",
      element: <Navigate to="/404" replace />,
    },
  ]);
}

// Auth
const Login = Loadable(lazy(() => import("../pages/login/Login")));
const Register = Loadable(lazy(() => import("../pages/register/Register")));
const ProfilePage = Loadable(lazy(() => import("../pages/profile/pages/ProfilePage")));

// Dashboard
const ViabilityStudy = Loadable(lazy(() => import("../pages/viabilityStudy/ViabilityStudy")));
const ViabilityStudyKMZ = Loadable(lazy(() => import("../pages/viabilityStudy/ViabilityStudyKMZ")));
const Studies = Loadable(lazy(() => import("../pages/studies/Studies")));
const Study = Loadable(lazy(() => import("../pages/study/Study")));
const VersionGroup = Loadable(lazy(() => import("../pages/version-group/VersionGroup")));
const NewVersion = Loadable(lazy(() => import("../pages/newVersion/NewVersion")));
const KmzError = Loadable(lazy(() => import("../pages/kmzError/KmzError")));
const PDFPage = Loadable(lazy(() => import("../pages/pdf-page/PDFPage")));
